'use client';
import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Package, FileText, Wallet, Truck } from 'lucide-react';

interface RealtimeNotificationContextType {
  latestOrderUpdate: { orderId: string; status: string; timestamp: number } | null;
  latestBidUpdate: { bidId: string; orderId: string; status: string; timestamp: number } | null;
  latestEscrowUpdate: { escrowId: string; orderId: string; status: string; timestamp: number } | null;
}

const RealtimeNotificationContext = createContext<RealtimeNotificationContextType>({
  latestOrderUpdate: null,
  latestBidUpdate: null,
  latestEscrowUpdate: null,
});

export const useRealtimeNotifications = () => useContext(RealtimeNotificationContext);

const ORDER_STATUS_LABELS: Record<string, string> = {
  open: 'Open for bids',
  bidding: 'Receiving bids',
  accepted: 'Traveler accepted',
  in_transit: 'In transit',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  disputed: 'Under dispute',
};

const ESCROW_STATUS_LABELS: Record<string, string> = {
  awaiting_payment: 'Awaiting payment',
  escrow_held: 'Funds held in escrow',
  delivery_confirmed: 'Delivery confirmed',
  released_to_traveler: 'Funds released to traveler',
  refunded: 'Refunded to customer',
  disputed: 'Under dispute',
};

export function RealtimeNotificationProvider({ children }: { children: React.ReactNode }) {
  const { user, userRole } = useAuth();
  const supabase = createClient();
  const channelsRef = useRef<ReturnType<typeof supabase.channel>[]>([]);

  const [latestOrderUpdate, setLatestOrderUpdate] = useState<RealtimeNotificationContextType['latestOrderUpdate']>(null);
  const [latestBidUpdate, setLatestBidUpdate] = useState<RealtimeNotificationContextType['latestBidUpdate']>(null);
  const [latestEscrowUpdate, setLatestEscrowUpdate] = useState<RealtimeNotificationContextType['latestEscrowUpdate']>(null);

  const cleanup = useCallback(() => {
    channelsRef.current.forEach(ch => supabase.removeChannel(ch));
    channelsRef.current = [];
  }, []);

  useEffect(() => {
    if (!user?.id) {
      cleanup();
      return;
    }

    cleanup();

    // ─── 1. Order status updates (customer sees their orders change status) ───
    const orderChannel = supabase
      .channel(`rt_orders_customer_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `customer_id=eq.${user.id}`,
        },
        (payload) => {
          const order = payload.new as any;
          const label = ORDER_STATUS_LABELS[order.status] || order.status;
          toast.success(`Order ${order.order_number}: ${label}`, {
            icon: React.createElement(Package, { size: 16 }),
            duration: 5000,
          });
          setLatestOrderUpdate({ orderId: order.id, status: order.status, timestamp: Date.now() });
        }
      )
      .subscribe();

    channelsRef.current.push(orderChannel);

    // ─── 2. Bid updates (customer sees new bids on their orders) ───
    const bidInsertChannel = supabase
      .channel(`rt_bids_customer_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bids',
        },
        async (payload) => {
          const bid = payload.new as any;
          // Check if this bid is on one of the user's orders
          const { data: order } = await supabase
            .from('orders')
            .select('order_number, customer_id')
            .eq('id', bid.order_id)
            .eq('customer_id', user.id)
            .maybeSingle();

          if (order) {
            toast.info(`New bid on ${order.order_number} — KES ${bid.proposed_fee}`, {
              icon: React.createElement(FileText, { size: 16 }),
              duration: 5000,
            });
            setLatestBidUpdate({ bidId: bid.id, orderId: bid.order_id, status: bid.status, timestamp: Date.now() });
          }
        }
      )
      .subscribe();

    channelsRef.current.push(bidInsertChannel);

    // ─── 3. Bid status updates (traveler sees their bid accepted/rejected) ───
    const bidUpdateChannel = supabase
      .channel(`rt_bids_traveler_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bids',
          filter: `traveler_id=eq.${user.id}`,
        },
        (payload) => {
          const bid = payload.new as any;
          if (bid.status === 'accepted') {
            toast.success('Your bid was accepted! 🎉 Check your orders.', {
              icon: React.createElement(FileText, { size: 16 }),
              duration: 6000,
            });
          } else if (bid.status === 'rejected') {
            toast.error('Your bid was not selected for this order.', {
              duration: 4000,
            });
          }
          setLatestBidUpdate({ bidId: bid.id, orderId: bid.order_id, status: bid.status, timestamp: Date.now() });
        }
      )
      .subscribe();

    channelsRef.current.push(bidUpdateChannel);

    // ─── 4. Escrow payment status changes ───
    const escrowChannel = supabase
      .channel(`rt_escrow_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'escrow_payments',
        },
        async (payload) => {
          if (payload.eventType === 'DELETE') return;
          const escrow = payload.new as any;

          // Only notify if this user is the customer or traveler
          if (escrow.customer_id !== user.id && escrow.traveler_id !== user.id) return;

          const label = ESCROW_STATUS_LABELS[escrow.escrow_status] || escrow.escrow_status;
          toast.success(`Payment update: ${label}`, {
            icon: React.createElement(Wallet, { size: 16 }),
            duration: 5000,
          });
          setLatestEscrowUpdate({
            escrowId: escrow.id,
            orderId: escrow.order_id,
            status: escrow.escrow_status,
            timestamp: Date.now(),
          });
        }
      )
      .subscribe();

    channelsRef.current.push(escrowChannel);

    // ─── 5. Delivery tracking updates (traveler's assigned orders) ───
    if (userRole === 'traveler') {
      const deliveryChannel = supabase
        .channel(`rt_delivery_traveler_${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'orders',
            filter: `accepted_traveler_id=eq.${user.id}`,
          },
          (payload) => {
            const order = payload.new as any;
            const label = ORDER_STATUS_LABELS[order.status] || order.status;
            toast.info(`Your delivery ${order.order_number}: ${label}`, {
              icon: React.createElement(Truck, { size: 16 }),
              duration: 5000,
            });
            setLatestOrderUpdate({ orderId: order.id, status: order.status, timestamp: Date.now() });
          }
        )
        .subscribe();

      channelsRef.current.push(deliveryChannel);
    }

    return cleanup;
  }, [user?.id, userRole]);

  return (
    <RealtimeNotificationContext.Provider value={{ latestOrderUpdate, latestBidUpdate, latestEscrowUpdate }}>
      {children}
    </RealtimeNotificationContext.Provider>
  );
}
